import { Form, Input, Button } from 'antd';
import logo from '../assets/audidox-logo.jpeg';
import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails
} from 'amazon-cognito-identity-js';
import {tokenStorage} from "../utils/tokenStorage";
import {user_pool_id, client_id} from "../utils/url_constants"
import LoadingPopup from "../components/LoadingPopup";
import ApiClient from "../api";
import {useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import { useWebSocket } from '../WebSocketContext';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

// Cognito pool configuration
const poolData = {
    UserPoolId: user_pool_id,
    ClientId: client_id
};
const userPool = new CognitoUserPool(poolData);
const apiclient = new ApiClient();

const Login = () => {
    // Access connectWebSocket from WebSocket context
    const { connectWebSocket } = useWebSocket();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState(false);

    // MSAL CONFIG
    const { instance, accounts } = useMsal();

    /**
     * Shared post-authentication validator and session bootstrap
     */
    const validateAndLoginUser = async (userEmail) => {
        const user_access = await apiclient.get_user_access(userEmail);
        console.log("User access payload:", user_access);
        const today = new Date();
        const licenseExpires = new Date(user_access?.license_expires);

        // Validate user access data
        if (!user_access || Object.keys(user_access).length === 0) {
            throw new Error("No roles found for your email address. Please contact your account administrator.");
        }
        if (user_access.active !== 1 && user_access.active !== true) {
            throw new Error("Your account is inactive. Please contact support.");
        }
        if (licenseExpires < today) {
            throw new Error(`Your license has expired on ${licenseExpires.toDateString()}. Please renew your license.`);
        }
        if (!user_access.role || user_access.role === "") {
            throw new Error("No roles found for your email address. Please contact your account administrator.");
        }

        // All checks passed: store user access and email in localStorage
        localStorage.setItem('user_access', JSON.stringify(user_access));
        localStorage.setItem('email', user_access.email);

        setTimeout(() => {
            connectWebSocket();
            setDashboard(true);
        }, 100);
    };

    const handleLogin = async() =>{
        setLoading(true);
        if (username.length > 2 && password.length > 2) {
            const authDetails = new AuthenticationDetails({
                Username: username,
                Password: password
            });
            const cognitoUser = new CognitoUser({
                Username: username,
                Pool: userPool
            });

            localStorage.setItem('lastUsername', username);

            cognitoUser.authenticateUser(authDetails, {
                onSuccess: async (cognitoOutput) => {
                    try {
                        // Store tokens in memory (not localStorage)
                        const expiresIn = cognitoOutput.idToken.payload.exp -
                            Math.floor(Date.now() / 1000);
                        tokenStorage.setTokens(
                            cognitoOutput.idToken.jwtToken,
                            cognitoOutput.accessToken.jwtToken,
                            cognitoOutput.refreshToken.token,
                            expiresIn,
                            'cognito'
                        );

                        await validateAndLoginUser(username);
                    } catch (error) {
                        console.error("Error fetching user access:", error);
                    } finally {
                        setLoading(false);
                    }
                },
                onFailure: (err) => {
                    setLoading(false);
                    console.log(err.message);
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    const newPassword = prompt("Please enter your new password:");
                    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
                        onSuccess: (result) => {
                            console.log(result);
                            console.log('Password changed successfully!');
                        },
                        onFailure: (err) => {
                            console.log(err.message);
                        }
                    });
                }
            });
        } else {
            setLoading(false);
            alert('Username and password must be longer than 2 characters');
        }
    }

    const WindowsIcon = () => (
        <svg width="18" height="18" viewBox="0 0 88 88" xmlns="http://www.w3.org/2000/svg">
            <path fill="#ffffff" d="M0 12.4L36 7.5V42H0V12.4zM40 7L88 0V42H40V7zM0 46H36V80.5L0 75.6V46zM40 46H88V88L40.5 81.2 40 46z"/>
        </svg>
    );

    /**
     * 2. Microsoft Entra ID (MSAL) Authentication Flow Trigger
     */
    const handleEntraLogin = async () => {
        try {
            await instance.loginRedirect(loginRequest);
            setLoading(true);
        } catch (error) {
            console.error("MSAL Redirect Error:", error);
            setLoading(false);
        }
    };

    const triggerLogin = async (e) => {
        if (e.keyCode === 13) {
            await handleLogin();
        }
    };


    /**
     * MSAL Post-Redirect Handler: Runs when returning from Active Directory
     */
    useEffect(() => {
        if (accounts.length > 0) {
            const activeAccount = accounts[0];
            setLoading(true);

            instance.acquireTokenSilent({
                scopes: ['api://aea2a581-3f0e-49ba-a678-781c80570d39/access_as_user'],
                account: activeAccount,
            })
                .then(async (response) => {
                    const expiresIn = Math.floor((response.expiresOn.getTime() - Date.now()) / 1000);

                    // 1. Save tokens to tokenStorage with 'entra' provider flag
                    tokenStorage.setTokens(
                        response.idToken,
                        response.accessToken,
                        null, // Refresh token is safely managed internally by MSAL
                        expiresIn,
                        'entra'
                    );

                    // 2. Extract email address
                    const userEmail = activeAccount.username || response.idTokenClaims?.email || response.idTokenClaims?.preferred_username;
                    localStorage.setItem('lastUsername', userEmail);

                    console.log('userEmail AD', userEmail);

                    // 3. Complete role/license check against backend API & redirect to dashboard
                    await validateAndLoginUser(userEmail);
                })
                .catch((error) => {
                    console.warn('Silent token acquisition failed:', error);
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect({
                            scopes: ['api://aea2a581-3f0e-49ba-a678-781c80570d39/access_as_user'],
                            account: activeAccount,
                        });
                    } else {
                        setLoading(false);
                    }
                });
        }
    }, [accounts, instance]);

    if (dashboard) return <Navigate to="/Dashboard" />;

    return (
        <div>
            <div className="login-container">
                <div className="login-card">
                    {/* Centered Logo with minimum width requirement  */}
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <img
                            src={logo}
                            alt="AudiDox Logo"
                            style={{ width: '250px', display: 'block', margin: '0 auto' }}
                        />
                    </div>

                    <LoadingPopup isVisible={loading} message={'Logging you in...'}/>

                    <Form
                        name="login_form"
                        layout="vertical"
                        requiredMark={false}
                    >
                        <Form.Item
                            label={<span style={{ color: '#A0B5B4' }}>Username</span>}
                            name="username"
                            rules={[{ required: true, message: 'Please enter your username' }]}
                        >
                            <Input placeholder="Enter username" size="large"
                                   value={username}
                                   onKeyDown={triggerLogin}
                                   onChange={(e) => setUsername(e.target.value)} />
                        </Form.Item>

                        <Form.Item
                            label={<span style={{ color: '#A0B5B4' }}>Password</span>}
                            name="password"
                            rules={[{ required: true, message: 'Please enter your password' }]}
                        >
                            <Input.Password placeholder="Enter password" size="large"
                                            value={password}
                                            onKeyDown={triggerLogin}
                                            onChange={(e) => setPassword(e.target.value)} />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 40 }}>
                            <Button type="primary" onClick={()=>handleLogin()} block size="large">
                                Login
                            </Button>
                        </Form.Item>

                        <Button
                                style={{ marginTop: 5, backgroundColor:'darkblue',borderWidth: 0 }}
                                icon={<WindowsIcon/>}
                                onClick={()=>handleEntraLogin()} block size="large">
                            SIGN IN WITH ACTIVE DIRECTORY
                        </Button>

                    </Form>
                </div>
            </div>
        </div>
    );
}

export default Login;
